import React, { useState } from "react";
import { columns } from "./collectionOrderList/columns";
import { DataTable } from "./collectionOrderList/data-table";
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { DataTablePagination } from "@/components/Pagination";
import { DataTableViewOptions } from "@/components/ColumnToggle";
import { useCollectionOrders } from "@/hooks/collectionOrder/useCollectionOrders";
import AddCollectionOrder from "./addCollectionOrder/AddCollectionOrder";
import Container from "@/components/Container";
import Header from "@/components/Header";
import Delete from "./collectionOrderList/Delete";
import Filters from "./collectionOrderList/filters/Filters";
import StatusDropdown from "./collectionOrderList/filters/StatusDropdown";
import { updateCollectionorderApi } from "@/api/collectionOrder";
import ExportCollection from "./collectionOrderList/ExportCollection";
import { DateRange } from "react-day-picker";
import Spinner from "@/components/Spinner";
import CollectionUpdate from "./collectionUpdate/CollectionUpdate";

const CollectionOrders: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    selectedWilaya: "",
    status: "",
    selectedCollector: "",
    dateRange: undefined as DateRange | undefined,
  });

  const { collectionOrders, refresh, meta, loading, error } =
    useCollectionOrders(
      pageIndex + 1,
      pageSize,
      searchTerm,
      filters.selectedWilaya,
      filters.status,
      filters.selectedCollector,
      filters.dateRange
    );

  const table = useReactTable({
    data: collectionOrders || [],
    columns: [
      ...columns,
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <div className="w-[190px]">
            <StatusDropdown
              isTable
              value={row.getValue("status")}
              onValueChange={(value) =>
                handleStatusChange(row.original.id?.toString(), value)
              }
            />
          </div>
        ),
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <div className="flex justify-end gap-2">
            {row.original.status === "Awaiting Collection" && (
              <CollectionUpdate
                onAddCollectionOrder={refresh}
                collectionId={row.original.id?.toString()}
              />
            )}
            <Delete
              collectionId={row.original.id?.toString()}
              onDelete={refresh}
            />
          </div>
        ),
      },
    ],
    pageCount: meta ? meta.lastPage : -1,
    manualPagination: true,
    state: { pagination: { pageIndex, pageSize } },
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const handleStatusChange = async (id: string, value: string) => {
    try {
      await updateCollectionorderApi(id, { status: value });
      refresh();
    } catch (error) {
      console.error("Failed to update status:", error);
      alert("An error occurred while updating the status.");
    }
  };

  const handleFiltersChange = (newFilters: {
    selectedWilaya: string;
    status: string;
    selectedCollector: string;
    dateRange: DateRange | undefined;
  }) => setFilters(newFilters);

  return (
    <Container isSmallScreenReady>
      <Header placeholder="Search Client name" setSearchTerm={setSearchTerm} />
      <div className="flex flex-col gap-2 p-4 h-full">
        <div className="flex justify-between items-center">
          <h1 className="font-semibold text-[18px]">Collection orders List</h1>
          <div className="flex justify-end items-center gap-4">
            <Filters onFiltersChange={handleFiltersChange} />
            <ExportCollection />
            <AddCollectionOrder onAddCollectionOrder={refresh} />
          </div>
        </div>

        <div className="flex-grow overflow-y-scroll py-2">
          <DataTable
            table={table}
            columns={columns}
            loading={loading}
            error={error}
          />
        </div>

        {collectionOrders && collectionOrders.length !== 0 && (
          <div className="mt-auto flex flex-col md:flex-row justify-between pt-2">
            <div className="hidden md:block">
              <DataTableViewOptions table={table} />
            </div>
            <DataTablePagination
              table={table}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </div>
    </Container>
  );
};

export default CollectionOrders;
